import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ExternalLink,
  Info,
  Loader2,
  PlugZap,
  Store,
  Unplug,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/app/hooks';
import { addMessage } from '@/features/chat/chatSlice';
import { store } from '@/app/store';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import ConfirmAlertDialog from '@/components/ui/confirm-alert-dialog';
import { useToast } from '@/hooks/use-toast';
import i18n from '@/i18n';
import { analytics } from '@/services/analytics';
import { getAiProposedUpdate } from '@/services/api';
import { logging } from '@/services/logging';
import {
  disconnectEtsyShop,
  fetchConnectedEtsyShops,
  startEtsyOAuth,
  type EtsyShop,
} from '@/services/etsyService';
import { WORKSPACE_CANVAS_MODES } from '@/components/workspace/workspaceMode';
import EtsyWorkspaceNav from '@/components/etsy/EtsyWorkspaceNav';

const formatDateTime = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const EtsySetupCanvas = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [shops, setShops] = useState<ReadonlyArray<EtsyShop>>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [actionShopId, setActionShopId] = useState<string | null>(null);

  const oauthStatus = searchParams.get('status');
  const oauthShopName = searchParams.get('shopName');
  const oauthError = searchParams.get('error');
  const oauthConnectedCount = Number(searchParams.get('connectedCount') || '0');

  const loadShops = useCallback(async (): Promise<ReadonlyArray<EtsyShop>> => {
    try {
      setStatus('loading');
      setError(null);
      const result = await fetchConnectedEtsyShops();
      setShops(result);
      setStatus('idle');
      return result;
    } catch (err) {
      logging.error('Failed to load Etsy shops', { error: err });
      const message = err instanceof Error ? err.message : 'Failed to load Etsy shops.';
      setError(message);
      setStatus('error');
      toast({ title: t('integrations.etsy.toasts.loadShopsFailedTitle', 'Failed to load Etsy shops'), description: message, variant: 'destructive' });
      return [];
    }
  }, [t, toast]);

  useEffect(() => {
    void loadShops();
  }, [loadShops]);

  useEffect(() => {
    if (!oauthStatus && !oauthError) {
      return;
    }

    if (oauthStatus === 'connected') {
      toast({
        title: t('integrations.etsy.toasts.connectedTitle', 'Etsy connected'),
        description: oauthConnectedCount > 1
          ? t('integrations.etsy.toasts.connectedMany', '{{count}} Etsy shops are now connected to TulasiHome. Disconnect any shops you do not need.', { count: oauthConnectedCount })
          : oauthShopName
            ? t('integrations.etsy.toasts.connectedOneNamed', '{{shopName}} is now connected to TulasiHome.', { shopName: oauthShopName })
            : t('integrations.etsy.toasts.connectedOne', 'Your Etsy shop is now connected to TulasiHome.'),
      });
      void loadShops();
    }

    if (oauthStatus === 'error' || oauthError) {
      const isNoShopsWarning = oauthError === 'No Etsy shops found for this user';
      toast({
        title: isNoShopsWarning
          ? t('integrations.etsy.toasts.noShopsFoundTitle', 'No Etsy shops found')
          : t('integrations.etsy.toasts.connectionFailedTitle', 'Etsy connection failed'),
        description: oauthError || t('integrations.etsy.toasts.connectionFailedDescription', 'We could not complete Etsy authorization.'),
        variant: isNoShopsWarning ? 'default' : 'destructive',
      });
    }

    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('status');
      next.delete('shopName');
      next.delete('connectedCount');
      next.delete('error');
      return next;
    }, { replace: true });
  }, [loadShops, oauthConnectedCount, oauthError, oauthShopName, oauthStatus, setSearchParams, toast]);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      analytics.track('button: ui.etsy_connect_click');
      const result = await startEtsyOAuth();
      window.location.assign(result.url);
    } catch (err) {
      logging.error('Failed to start Etsy OAuth', { error: err });
      const message = err instanceof Error ? err.message : 'Failed to start Etsy authorization.';
      toast({ title: t('integrations.etsy.toasts.connectFailedTitle', 'Unable to connect Etsy'), description: message, variant: 'destructive' });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (shop: EtsyShop) => {
    try {
      setActionShopId(shop.id);
      await disconnectEtsyShop(shop.id);
      toast({
        title: t('integrations.etsy.toasts.disconnectedTitle', 'Etsy disconnected'),
        description: t('integrations.etsy.toasts.disconnectedDescription', '{{shopName}} has been disconnected.', { shopName: shop.shopName }),
      });
      await loadShops();
    } catch (err) {
      logging.error('Failed to disconnect Etsy shop', { error: err, shopId: shop.id });
      const message = err instanceof Error ? err.message : 'Failed to disconnect Etsy shop.';
      toast({ title: t('integrations.etsy.toasts.disconnectFailedTitle', 'Disconnect failed'), description: message, variant: 'destructive' });
    } finally {
      setActionShopId(null);
    }
  };

  const handleAccessInfo = useCallback(() => {
    analytics.track('button: ui.etsy_access_ai_help');
    toast({
      title: t('integrations.etsy.toasts.aiHelpTitle', 'AI response incoming'),
      description: t('integrations.etsy.toasts.aiHelpDescription', 'Please check the chat. The AI will explain what Etsy access means and the next steps.'),
    });

    const aiPrompt = 'Explain in simple language what Etsy access TulasiHome needs during setup. Cover shop details, orders and receipts, buyer shipping address, buyer email, listings, and tracking updates. Keep it concise, practical, and seller-friendly.';
    const fallbackMessage = 'TulasiHome needs Etsy access to connect your shop, read orders and buyer details, view listings, and send tracking updates back to Etsy.';

    void (async () => {
      try {
        const userLanguage = i18n.language;
        const localizedInstruction = `${aiPrompt} Reply in the user's language identified by the code "${userLanguage}".`;
        const proposed = await getAiProposedUpdate(
          [{ role: 'user', content: localizedInstruction }],
          store.getState().order as unknown as Record<string, unknown>,
          userLanguage,
          { source: 'interface', promptMode: 'external_integrations' },
        );

        const utterance = proposed.utterance?.trim();
        if (utterance) {
          dispatch(addMessage({ author: 'ai', content: utterance }));
          return;
        }
      } catch (promptError) {
        logging.error('Failed to fetch Etsy access AI explanation', { error: promptError });
      }

      dispatch(addMessage({ author: 'ai', content: fallbackMessage }));
    })();
  }, [dispatch, toast]);

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Store className="h-3.5 w-3.5" />
              Etsy
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              {t('integrations.etsy.setupTitle', 'Etsy Setup')}
            </h1>
            <p className="max-w-3xl text-sm text-muted-foreground">
              {t(
                'integrations.etsy.setupDescription',
                'Connect your Etsy shops and review recent sync activity. If more than one shop is imported, disconnect the ones you do not need. This page is seller-facing and does not change your internal production flow.'
              )}
            </p>
          </div>
        </div>
        <Button onClick={() => void handleConnect()} disabled={connecting}>
          {connecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />}
          {t('integrations.etsy.connect', 'Connect Etsy shop')}
        </Button>
      </div>

      <EtsyWorkspaceNav activeMode={WORKSPACE_CANVAS_MODES.etsySetup} />

      <div className="grid min-h-0 flex-1 gap-6">
        <Card className="min-h-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {t('integrations.etsy.connectedShops', 'Connected shops')}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={handleAccessInfo}
                      aria-label={t('integrations.etsy.permissionsInfo', 'What does Etsy access mean?')}
                    >
                      <Info className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('integrations.etsy.permissionsInfo', 'Ask AI what Etsy access means')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            <CardDescription>
              {t('integrations.etsy.connectedShopsDescription', 'Each connected shop can sync imported Etsy orders into TulasiHome.')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {status === 'loading' && shops.length === 0 ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading', 'Loading')}
              </div>
            ) : null}

            {error && shops.length === 0 ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            ) : null}

            {status !== 'loading' && shops.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-muted/20 px-6 py-10 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-card shadow-sm">
                  <Store className="h-5 w-5 text-muted-foreground" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">
                  {t('integrations.etsy.noShopsTitle', 'No Etsy shop connected yet')}
                </h2>
                <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
                  {t(
                    'integrations.etsy.noShopsDescription',
                    'Use the connect button to authorize Etsy and register your seller shop in TulasiHome.'
                  )}
                </p>
              </div>
            ) : null}

            {shops.map((shop) => {
              const isBusy = actionShopId === shop.id;
              return (
                <div key={shop.id} className="rounded-xl border border-border/70 bg-card p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-lg font-semibold text-foreground">{shop.shopName}</h3>
                        <Badge variant={shop.isActive ? 'default' : 'secondary'}>
                          {shop.isActive
                            ? t('integrations.etsy.shopStatus.active', 'Active')
                            : t('integrations.etsy.shopStatus.inactive', 'Inactive')}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <span>ID: {shop.etsyShopId}</span>
                        {shop.shopUrl ? (
                          <a
                            href={shop.shopUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                            onClick={() => analytics.track('link: ui.etsy_shop_open', { shopId: shop.id, etsyShopId: shop.etsyShopId })}
                          >
                            {t('integrations.etsy.openShop', 'Open shop')}
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        ) : null}
                      </div>
                    </div>

                    <div className="ml-auto flex flex-wrap justify-end gap-2">
                      <ConfirmAlertDialog
                        title={t('integrations.etsy.disconnectTitle', 'Disconnect Etsy shop')}
                        description={t(
                          'integrations.etsy.disconnectDescription',
                          'This will disable the current Etsy connection for this shop until you reconnect it.'
                        )}
                        confirmText={t('integrations.etsy.disconnectConfirm', 'Disconnect')}
                        cancelText={t('common.cancel', 'Cancel')}
                        onConfirm={async () => {
                          analytics.track('button: ui.etsy_disconnect_confirm', { shopId: shop.id });
                          await handleDisconnect(shop);
                        }}
                        trigger={
                          <Button
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            disabled={isBusy}
                            onClick={() => analytics.track('button: ui.etsy_disconnect_click', { shopId: shop.id })}
                          >
                            <Unplug className="mr-2 h-4 w-4" />
                            {t('integrations.etsy.disconnect', 'Disconnect')}
                          </Button>
                        }
                      />
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('integrations.etsy.setup.connectedAt', 'Connected')}</div>
                      <div className="mt-1 text-sm text-foreground">{formatDateTime(shop.connectedAt) ?? t('integrations.etsy.common.never', 'Never')}</div>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('integrations.etsy.setup.lastSync', 'Last sync')}</div>
                      <div className="mt-1 text-sm text-foreground">{formatDateTime(shop.lastSyncAt) ?? t('integrations.etsy.common.never', 'Never')}</div>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('integrations.etsy.setup.statusLabel', 'Status')}</div>
                      <div className="mt-1 text-sm text-foreground">{shop.isActive ? t('integrations.etsy.setup.readyToSync', 'Ready to sync') : t('integrations.etsy.setup.disconnectedStatus', 'Disconnected')}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EtsySetupCanvas;
